import type { Problem } from './generated'

/**
 * Ошибка по RFC 9457 — формат ответа gateway при любом не-2xx.
 *
 * Тип НЕ объявляем руками: он приезжает из кодогена, то есть прямо из OpenAPI-контракта
 * (`openapi/components/schemas/common.yaml`). Рукописная копия молча разъехалась бы с
 * контрактом при первом же его изменении — а ещё столкнулась бы с генерируемой в барреле
 * `shared/api/index.ts`, который реэкспортирует и то, и другое.
 *
 * Своего здесь только рантайм: кодоген отдаёт типы, но не проверки. Ответ 500 может прийти
 * от прокси в виде HTML, а не JSON, — поэтому доверять форме тела нельзя.
 */
export type { Problem }

/**
 * Наш собственный `Problem.type`: сервер ответил успехом, но не JSON.
 *
 * Это не ошибка бекенда, а почти всегда ошибка РАЗДАЧИ: `/api` не проксируется на gateway,
 * и SPA-фолбэк (`try_files $uri $uri/ /index.html`) отдаёт `index.html` на любой путь —
 * включая `/api/me`. Приходит `200 text/html`, и без этой проверки приложение приняло бы
 * HTML-страницу за данные (см. `http.ts`).
 *
 * URI наш, поэтому по нему можно безопасно узнавать этот случай (`classify.ts`).
 */
export const API_NOT_JSON = 'https://prima.ru/errors/api-not-json'

export function parseProblem(body: unknown, status: number): Problem {
  if (body !== null && typeof body === 'object') {
    const b = body as Record<string, unknown>
    if (typeof b.title === 'string') {
      return {
        // Спред сохраняет extension-члены RFC 9457 (например, `currentVersion` в 409-ответе на
        // сохранение черновика сценария) — фронт вправе на них опереться, и раньше они молча
        // срезались. Известные поля ниже перетирают спред нормализованными значениями.
        // Object-spread безопасен от prototype pollution: копирует только собственные перечислимые
        // свойства как data-свойства, поэтому `__proto__` станет обычным ключом, а не прототипом
        // (в отличие от присваивания `obj[k] = …`).
        ...b,
        type: typeof b.type === 'string' ? b.type : 'about:blank',
        title: b.title,
        detail: typeof b.detail === 'string' ? b.detail : undefined,
        status: typeof b.status === 'number' ? b.status : status,
        instance: typeof b.instance === 'string' ? b.instance : undefined,
        requestId: typeof b.requestId === 'string' ? b.requestId : undefined,
      }
    }
  }
  // Тело не похоже на Problem — например, HTML от прокси или пустой ответ.
  return { type: 'about:blank', title: `HTTP ${status}`, status }
}

export function isProblem(value: unknown): value is Problem {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof (value as Problem).title === 'string' &&
    typeof (value as Problem).status === 'number'
  )
}

/**
 * Человеческий текст ошибки для UI: заголовок Problem (RFC 9457), затем `.message` обычной
 * ошибки, иначе — переданный фолбэк. Фолбэк обязателен и специфичен для места вызова, поэтому
 * он параметр, а не общий дефолт.
 */
export function problemText(error: unknown, fallback: string): string {
  if (isProblem(error)) return error.title
  if (error instanceof Error) return error.message
  return fallback
}

/**
 * Пояснение сервера (`Problem.detail`) — ВТОРОЙ строкой под заголовком, если оно пришло.
 *
 * ⚠️ Заводится не для полноты, а потому что есть отказы, у которых весь смысл лежит именно здесь.
 * Разбор файла базы гостей — такой: заголовок говорит «Файл не читается», а `detail` — «это таблица
 * старого формата .xls, сохраните как .xlsx». Спека clients-service (§10) прямо просит показывать
 * его как есть: что именно не так с файлом, знает только сервер, и общая фраза вместо его
 * объяснения отправляет менеджера гадать.
 *
 * Показывать МОЖНО НЕ ВЕЗДЕ: `detail` — свободный текст для человека, а не поле для разбора, и у
 * многих ручек он либо пуст, либо повторяет заголовок. Поэтому это отдельная функция, а не добавка
 * к `problemText`: место вызова решает, нужна ли ему вторая строка.
 */
export function problemDetail(error: unknown): string | undefined {
  if (!isProblem(error)) return undefined
  const detail = error.detail?.trim()
  // Пустое и дословно повторяющее заголовок пояснение — это не вторая строка, а шум.
  return detail && detail !== error.title.trim() ? detail : undefined
}
